import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  SimpleButton,
  LoadingButton,
  TextArea,
  Select,
  NotificationsList,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsCheckCircle, BsXCircle, BsClockHistory, BsGlobe, BsFileEarmarkText, BsPaperclip } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import PppDialog from "./PppDialog";

const ESITO_CFG = {
  ammessa:  { cls: "bg-green-100 text-green-800",  label: "Ammessa",  icon: <BsCheckCircle /> },
  respinta: { cls: "bg-red-100 text-red-800",      label: "Respinta", icon: <BsXCircle /> },
};

const ESITO_OPTIONS = [
  { value: "ammessa",  label: "Ammessa — avanzamento a Fase 13 (PFTE)" },
  { value: "respinta", label: "Respinta — chiusura procedimento" },
];

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function EsitoBadge({ esito }) {
  const c = ESITO_CFG[esito];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${c.cls}`}>
      {c.icon} {c.label}
    </span>
  );
}

function TermineBar({ dataAvvio, termine60gg, termine90gg }) {
  if (!termine60gg) return null;
  const oggi = new Date();
  const t60  = new Date(termine60gg);
  const t90  = termine90gg ? new Date(termine90gg) : null;
  const giorni60 = Math.ceil((t60 - oggi) / 86400000);
  const scaduto60 = giorni60 < 0;
  const giorni90  = t90 ? Math.ceil((t90 - oggi) / 86400000) : null;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
      <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Termini procedurali</p>
      <div className="flex flex-wrap gap-4">
        <div>
          <span className="text-gray-400">Avvio: </span>
          <span className="font-medium">{formatDate(dataAvvio)}</span>
        </div>
        <div>
          <span className="text-gray-400">Termine 60 gg: </span>
          <span className={`font-medium ${scaduto60 ? "text-red-600" : giorni60 <= 10 ? "text-orange-600" : "text-gray-700"}`}>
            {formatDate(termine60gg)}
            {!scaduto60 && ` (${giorni60} gg)`}
            {scaduto60 && " — scaduto"}
          </span>
        </div>
        {t90 && (
          <div>
            <span className="text-gray-400">Proroga 90 gg: </span>
            <span className="font-medium text-gray-700">
              {formatDate(termine90gg)}
              {giorni90 !== null && giorni90 >= 0 && ` (${giorni90} gg)`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentoAllegatoSection({ provvedimento, procedimentoId, propostaId, canGestisci, api, onRefresh }) {
  const { addToast } = useStateContext();
  const [openDialog, setOpenDialog]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [rimuovendo, setRimuovendo]   = useState(false);
  const [allegando, setAllegando]     = useState(false);
  const [tipoFile, setTipoFile]       = useState("delibera");
  const [dialogErrors, setDialogErrors] = useState(null);
  const fileRef = useRef(null);

  const TIPO_ATTO_OPTIONS = [
    { value: "delibera",      label: "Delibera" },
    { value: "determinazione", label: "Determinazione" },
    { value: "altro",         label: "Altro atto amministrativo" },
  ];

  const handleUploadEAllega = () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setDialogErrors(["Selezionare un file da allegare."]); return; }

    setDialogErrors(null);
    setUploading(true);

    // 1. Upload documento generale del procedimento
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tipo", tipoFile);
    fd.append("propostaId", propostaId);

    api.uploadDocumento(procedimentoId, fd, {
      onSuccess: (res) => {
        const docId = res?.id;
        if (!docId) { setUploading(false); setDialogErrors(["Errore durante il caricamento del file."]); return; }

        // 2. Associa il documento al provvedimento
        setAllegando(true);
        api.allegaDocumentoProvvedimento(procedimentoId, propostaId, provvedimento.id, { documento_id: docId }, {
          onSuccess: () => {
            setUploading(false);
            setAllegando(false);
            setOpenDialog(false);
            if (fileRef.current) fileRef.current.value = "";
            addToast({ type: "success", message: "Atto amministrativo allegato con successo." });
            onRefresh();
          },
          onFormError: (body, text) => {
            setUploading(false);
            setAllegando(false);
            setDialogErrors([text]);
          },
        });
      },
      onError: () => {
        setUploading(false);
        setDialogErrors(["Errore durante il caricamento del file."]);
      },
      onFormError: (body, text) => {
        setUploading(false);
        setDialogErrors([text]);
      },
    });
  };

  const handleRimuovi = () => {
    setRimuovendo(true);
    api.rimuoviDocumentoProvvedimento(procedimentoId, propostaId, provvedimento.id, {
      onSuccess: () => {
        setRimuovendo(false);
        addToast({ type: "success", message: "Allegato rimosso." });
        onRefresh();
      },
      onFormError: (body, text) => {
        setRimuovendo(false);
        addToast({ type: "error", message: text });
      },
    });
  };

  const hasDocumento = !!provvedimento.documento_atto_id;
  const downloadUrl = hasDocumento
    ? `/api/v1/ppp/procedimenti/${procedimentoId}/documenti/${provvedimento.documento_atto_id}/download`
    : null;

  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Atto amministrativo</p>
      {hasDocumento ? (
        <div className="flex items-center gap-3 flex-wrap">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <BsFileEarmarkText className="shrink-0" />
            {provvedimento.documento_atto_nome ?? "Scarica atto"}
          </a>
          {canGestisci && (
            <span
              onClick={!rimuovendo ? handleRimuovi : undefined}
              className={`text-xs text-red-500 hover:text-red-700 cursor-pointer ${rimuovendo ? "opacity-50 pointer-events-none" : ""}`}
            >
              {rimuovendo ? "Rimozione…" : "Rimuovi allegato"}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Nessun atto allegato.</span>
          {canGestisci && (
            <SimpleButton
              customFunc={() => { setDialogErrors(null); setOpenDialog(true); }}
              text="Allega atto"
              icon={<BsPaperclip />}
            />
          )}
        </div>
      )}

      {/* Dialog upload atto */}
      <PppDialog
        open={openDialog}
        title="Allega atto amministrativo"
        onClose={() => setOpenDialog(false)}
        footer={
          <>
            <SimpleButton customFunc={() => setOpenDialog(false)} text="Annulla" />
            <span className={uploading || allegando ? "opacity-50 pointer-events-none" : ""}>
              <LoadingButton
                isLoading={uploading || allegando}
                customFunc={handleUploadEAllega}
                text={allegando ? "Associando…" : "Carica e allega"}
              />
            </span>
          </>
        }
      >
        <div className="space-y-4">
          <NotificationsList errors={dialogErrors} />
          <p className="text-sm text-gray-600">
            Carica il documento che rappresenta l&apos;atto amministrativo di approvazione o respingimento
            (determinazione, delibera, ecc.). Sarà visibile all&apos;operatore economico dopo la pubblicazione
            del provvedimento.
          </p>
          <Select
            label="Tipo atto"
            value={tipoFile}
            onChange={(e) => setTipoFile(e.target.value)}
            options={TIPO_ATTO_OPTIONS}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.odt,.p7m"
              className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
        </div>
      </PppDialog>
    </div>
  );
}

function ProvvedimentoCard({ provvedimento, procedimentoId, propostaId, onInviaComunicazione, onPubblica, canGestisci, api, onRefresh }) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <EsitoBadge esito={provvedimento.esito} />
          {provvedimento.fase13_avviata && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
              Fase 13 avviata
            </span>
          )}
          {provvedimento.pubblicato && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded">
              <BsGlobe className="shrink-0" /> Pubblicato
            </span>
          )}
          {provvedimento.comunicazione_inviata && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
              Comunicazione inviata
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">
          Emesso il {formatDate(provvedimento.emesso_il)} da {provvedimento.emesso_da ?? "—"}
        </span>
      </div>

      <TermineBar
        dataAvvio={provvedimento.data_avvio}
        termine60gg={provvedimento.termine_60gg}
        termine90gg={provvedimento.termine_90gg}
      />

      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Motivazione</p>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{provvedimento.motivazione}</p>
      </div>

      {provvedimento.testo_provvedimento && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Testo del provvedimento</p>
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 max-h-40 overflow-y-auto whitespace-pre-wrap">
            {provvedimento.testo_provvedimento}
          </div>
        </div>
      )}

      <DocumentoAllegatoSection
        provvedimento={provvedimento}
        procedimentoId={procedimentoId}
        propostaId={propostaId}
        canGestisci={canGestisci}
        api={api}
        onRefresh={onRefresh}
      />

      {provvedimento.comunicazione_inviata && provvedimento.testo_comunicazione && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Comunicazione operatore ({formatDate(provvedimento.comunicazione_inviata_il)})
          </p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{provvedimento.testo_comunicazione}</p>
        </div>
      )}

      {provvedimento.pubblicato && provvedimento.url_pubblicazione && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">URL pubblicazione</p>
          <a
            href={provvedimento.url_pubblicazione}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline break-all"
          >
            {provvedimento.url_pubblicazione}
          </a>
        </div>
      )}

      {canGestisci && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          {!provvedimento.comunicazione_inviata && (
            <SimpleButton
              customFunc={() => onInviaComunicazione(provvedimento)}
              text="Invia comunicazione operatore"
            />
          )}
          {!provvedimento.pubblicato && (
            <SimpleButton
              customFunc={() => onPubblica(provvedimento)}
              text="Pubblica provvedimento"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function Fase12Tab({ procedimentoId, propostaId, canCrea, canGestisci }) {
  const api = usePppApi();
  const { addToast } = useStateContext();
  const [provvedimenti, setProvvedimenti] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);

  const [openCrea, setOpenCrea]           = useState(false);
  const [openComunicazione, setOpenComunicazione] = useState(null);
  const [openPubblica, setOpenPubblica]   = useState(null);

  const [creaErrors, setCreaErrors]               = useState(null);
  const [comunicazioneErrors, setComunicazioneErrors] = useState(null);
  const [pubblicaErrors, setPubblicaErrors]       = useState(null);

  const [creaForm, setCreaForm] = useState({
    esito: "",
    motivazione: "",
    testo_provvedimento: "",
  });
  const [comunicazioneForm, setComunicazioneForm] = useState({ testo_comunicazione: "" });
  const [pubblicaForm, setPubblicaForm]           = useState({ url_pubblicazione: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.listProvvedimentiFase12(procedimentoId, propostaId, {
      onSuccess: (r) => { setProvvedimenti(Array.isArray(r) ? r : (r?.data ?? [])); setLoading(false); },
      onError:   () => setLoading(false),
    });
  }, [procedimentoId, propostaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCrea = () => {
    setCreaErrors(null);
    const errs = [];
    if (!creaForm.esito) errs.push("Selezionare l'esito del provvedimento.");
    if (!creaForm.motivazione || creaForm.motivazione.length < 20) errs.push("La motivazione deve essere di almeno 20 caratteri.");
    if (errs.length) { setCreaErrors(errs); return; }

    setSaving(true);
    api.creaProvvedimentoFase12(procedimentoId, propostaId, {
      esito: creaForm.esito,
      motivazione: creaForm.motivazione,
      testo_provvedimento: creaForm.testo_provvedimento || undefined,
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenCrea(false);
        setCreaForm({ esito: "", motivazione: "", testo_provvedimento: "" });
        addToast({ type: "success", message: "Provvedimento emesso con successo." });
        load();
      },
      onFormError: (body, text) => {
        setSaving(false);
        setCreaErrors([text]);
      },
    });
  };

  const handleInviaComunicazione = () => {
    setComunicazioneErrors(null);
    if (!comunicazioneForm.testo_comunicazione || comunicazioneForm.testo_comunicazione.length < 10) {
      setComunicazioneErrors(["Il testo della comunicazione deve essere di almeno 10 caratteri."]);
      return;
    }
    setSaving(true);
    api.inviaComunicazioneProvvedimento(procedimentoId, propostaId, openComunicazione.id, comunicazioneForm, {
      onSuccess: () => {
        setSaving(false);
        setOpenComunicazione(null);
        setComunicazioneForm({ testo_comunicazione: "" });
        addToast({ type: "success", message: "Comunicazione inviata." });
        load();
      },
      onFormError: (body, text) => {
        setSaving(false);
        setComunicazioneErrors([text]);
      },
    });
  };

  const handlePubblica = () => {
    setPubblicaErrors(null);
    setSaving(true);
    api.pubblicaProvvedimentoFase12(procedimentoId, propostaId, openPubblica.id, pubblicaForm, {
      onSuccess: () => {
        setSaving(false);
        setOpenPubblica(null);
        setPubblicaForm({ url_pubblicazione: "" });
        addToast({ type: "success", message: "Provvedimento pubblicato." });
        load();
      },
      onFormError: (body, text) => {
        setSaving(false);
        setPubblicaErrors([text]);
      },
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-800">
            Fase 12 — Provvedimento di Ammissione o Respingimento
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">Art. 193, co. 6 D.lgs 36/2023 — termine 60 giorni (prorogabili a 90)</p>
        </div>
        {canCrea && provvedimenti.length === 0 && (
          <SimpleButton
            customFunc={() => setOpenCrea(true)}
            text="Emetti provvedimento"
          />
        )}
      </div>

      {provvedimenti.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">
          <BsClockHistory className="mx-auto mb-2 text-2xl" />
          Nessun provvedimento emesso per questa proposta.
        </div>
      ) : (
        <div className="space-y-4">
          {provvedimenti.map((p) => (
            <ProvvedimentoCard
              key={p.id}
              provvedimento={p}
              procedimentoId={procedimentoId}
              propostaId={propostaId}
              canGestisci={canGestisci}
              api={api}
              onRefresh={load}
              onInviaComunicazione={setOpenComunicazione}
              onPubblica={setOpenPubblica}
            />
          ))}
        </div>
      )}

      {/* Dialog — emetti provvedimento */}
      <PppDialog
        open={openCrea}
        title="Emetti provvedimento di ammissione / respingimento"
        onClose={() => setOpenCrea(false)}
        footer={
          <>
            <SimpleButton customFunc={() => setOpenCrea(false)} text="Annulla" />
            <LoadingButton isLoading={saving} customFunc={handleCrea} text="Emetti provvedimento" />
          </>
        }
      >
        <div className="space-y-4">
          <NotificationsList errors={creaErrors} />
          <Select
            label="Esito *"
            value={creaForm.esito}
            onChange={(e) => setCreaForm((f) => ({ ...f, esito: e.target.value }))}
            options={[{ value: "", label: "— Seleziona esito —" }, ...ESITO_OPTIONS]}
          />
          <TextArea
            label="Motivazione del provvedimento *"
            value={creaForm.motivazione}
            onChange={(e) => setCreaForm((f) => ({ ...f, motivazione: e.target.value }))}
            placeholder="Illustrare i motivi della decisione (min. 20 caratteri)..."
            rows={5}
          />
          <TextArea
            label="Testo integrale del provvedimento (facoltativo)"
            value={creaForm.testo_provvedimento}
            onChange={(e) => setCreaForm((f) => ({ ...f, testo_provvedimento: e.target.value }))}
            placeholder="Testo completo della determinazione / deliberazione..."
            rows={6}
          />
        </div>
      </PppDialog>

      {/* Dialog — invia comunicazione all'operatore */}
      <PppDialog
        open={!!openComunicazione}
        title="Invia comunicazione all'operatore economico"
        onClose={() => setOpenComunicazione(null)}
        footer={
          <>
            <SimpleButton customFunc={() => setOpenComunicazione(null)} text="Annulla" />
            <LoadingButton isLoading={saving} customFunc={handleInviaComunicazione} text="Invia comunicazione" />
          </>
        }
      >
        <div className="space-y-4">
          <NotificationsList errors={comunicazioneErrors} />
          {openComunicazione && (
            <div className="bg-gray-50 p-3 rounded text-sm">
              Esito: <EsitoBadge esito={openComunicazione.esito} />
            </div>
          )}
          <TextArea
            label="Testo della comunicazione formale *"
            value={comunicazioneForm.testo_comunicazione}
            onChange={(e) => setComunicazioneForm({ testo_comunicazione: e.target.value })}
            placeholder="Testo della comunicazione formale all'operatore economico..."
            rows={6}
          />
        </div>
      </PppDialog>

      {/* Dialog — pubblica provvedimento */}
      <PppDialog
        open={!!openPubblica}
        title="Pubblica provvedimento sul sito istituzionale"
        onClose={() => setOpenPubblica(null)}
        footer={
          <>
            <SimpleButton customFunc={() => setOpenPubblica(null)} text="Annulla" />
            <LoadingButton isLoading={saving} customFunc={handlePubblica} text="Conferma pubblicazione" />
          </>
        }
      >
        <div className="space-y-4">
          <NotificationsList errors={pubblicaErrors} />
          {openPubblica?.esito === "ammessa" && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
              La pubblicazione avvierà automaticamente la <strong>Fase 13</strong> (Integrazione e Approvazione PFTE).
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL pubblicazione (facoltativo)
            </label>
            <input
              type="url"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://..."
              value={pubblicaForm.url_pubblicazione}
              onChange={(e) => setPubblicaForm({ url_pubblicazione: e.target.value })}
            />
          </div>
        </div>
      </PppDialog>
    </div>
  );
}
